/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useState } from 'react';
import { Tabs } from 'antd';

import { TabContent } from '../../../utils/forms/FormInterface';

const { TabPane } = Tabs;

export type TabActiosProps = 'add' | 'remove';
export type TabActiosEventType =
  | React.MouseEvent<Element, MouseEvent>
  | React.KeyboardEvent<Element>;

interface TabCountContextValue {
  setTabCount: (count: number | undefined) => void;
  setTabCountText: (text: string) => void;
}

export const TabCountContext = createContext<TabCountContextValue>({
  setTabCount: () => {},
  setTabCountText: () => {},
});

export const useTabCount = () => useContext(TabCountContext);

interface AbstractTabsProps {
  activeKey: string;
  entityName: string;
  children: React.ReactNode;
  updateFormVisibility: (params: TabActiosProps, _targetKey: string | TabActiosEventType) => void;
  onTabClick: (key: string) => void;
  secondTabTitle?: string;
  showFormComponent?: boolean;
  content?: TabContent;
  formComponent?: React.ReactNode;
  storeInventoryLanding?: React.ReactNode;
  processedPhotoLanding?: React.ReactNode;
  variantPriceLanding?: React.ReactNode;
  storeAddOnsLanding?: React.ReactNode;
  deliverySlotLanding?: React.ReactNode;
  storeBulkAddOnsLanding?: React.ReactNode;
  storeDashboardLanding?: React.ReactNode;
  multipleTabTitle?: string[];
  tabCount?: number | undefined;
  tabCountText?: string;
}

const AbstractTabs = (props: AbstractTabsProps) => {
  const {
    storeInventoryLanding,
    processedPhotoLanding,
    variantPriceLanding,
    deliverySlotLanding,
    storeAddOnsLanding,
    storeBulkAddOnsLanding,
    storeDashboardLanding,
  } = props;

  const [tabCount, setTabCount] = useState<number | undefined>(props.tabCount);
  const [tabCountText, setTabCountText] = useState<string>(props.tabCountText || 'orders found');

  const contextValue: TabCountContextValue = {
    setTabCount,
    setTabCountText,
  };

  const PRIMARY = '#004195';
  const PRIMARY_SOFT = '#eaf1fb';

  const firstTabLabel = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <span>{props.entityName}</span>
      {tabCount !== undefined && tabCount !== null && (
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: PRIMARY,
            background: PRIMARY_SOFT,
            padding: '2px 10px',
            borderRadius: '999px',
            border: `1px solid ${PRIMARY}1a`,
            lineHeight: 1.5,
          }}
        >
          {tabCount} {tabCountText || 'orders found'}
        </span>
      )}
    </span>
  );
  const onTabClick = (key: string) => {
    props.onTabClick(key);
  };

  const onEdit = (_targetKey: string | TabActiosEventType, action: TabActiosProps) => {
    props.updateFormVisibility(action, _targetKey);
  };

  return (
    <TabCountContext.Provider value={contextValue}>
      <section className="abstractTabs">
        <Tabs
          hideAdd
          type="editable-card"
          activeKey={props.activeKey}
          onTabClick={onTabClick}
          onEdit={onEdit}
          size={'large'}
        >
          <TabPane tab={firstTabLabel} key="0" closable={false}>
            {props.children}
          </TabPane>
          {props.showFormComponent && (
            <TabPane tab={props.secondTabTitle} key={'a'} closable={true}>
              {props.formComponent}
            </TabPane>
          )}

          {storeInventoryLanding && (
            <TabPane tab="Store Inventory" key="store-inventory" closable={true}>
              {storeInventoryLanding}
            </TabPane>
          )}

          {variantPriceLanding && (
            <TabPane tab="Store Variant" key="store-variant" closable={true}>
              {variantPriceLanding}
            </TabPane>
          )}

          {storeAddOnsLanding && (
            <TabPane tab="Store Add-ons" key="store-addons" closable={true}>
              {storeAddOnsLanding}
            </TabPane>
          )}

          {storeBulkAddOnsLanding && (
            <TabPane tab="store-bulk-addons" key="store-bulk-addons" closable={true}>
              {storeBulkAddOnsLanding}
            </TabPane>
          )}

          {storeDashboardLanding && (
            <TabPane tab="store-dashboard" key="store-dashboard" closable={true}>
              {storeDashboardLanding}
            </TabPane>
          )}

          {deliverySlotLanding && (
            <TabPane tab="Delivery Slot" key="delivery-slot" closable={true}>
              {deliverySlotLanding}
            </TabPane>
          )}

          {props?.content?.length &&
            props.content.map((item: any) => (
              <TabPane tab={item.tabTitle} key={item.key} closable={true}>
                {item.content}
              </TabPane>
            ))}
        </Tabs>
      </section>
    </TabCountContext.Provider>
  );
};

export default AbstractTabs;
